import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

/**
 * Public-facing share URL: /share/doc/:token
 * - User must be authenticated (Guard handles redirect to login)
 * - Backend resolves token → doc_id; user is auto-added to `sent_to` if not owner
 * - We then redirect to /dashboard/documents?open={doc_id} and DocumentsPage opens it
 */
export default function DocShareLanding() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Stash the intended destination so we come back after login
      try { sessionStorage.setItem("post_login_redirect", `/share/doc/${token}`); } catch {}
      nav("/login");
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/documents/share/${token}`);
        if (r.data.copy) {
          toast.success(
            r.data.reused
              ? `Reopening your copy of "${r.data.title}"…`
              : `A private copy was created for you — edit freely!`
          );
        } else {
          toast.success(`Opening "${r.data.title || "shared document"}"…`);
        }
        nav(`/dashboard/documents?open=${r.data.doc_id}`);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not open shared document");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-rose-500 font-bold text-lg mb-1">Link unavailable</div>
          <div className="text-sm text-slate-500 mb-4">{error}</div>
          <button onClick={() => nav("/dashboard/documents")} className="btn-primary">Go to documents</button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-400 text-sm">Opening shared document…</div>
    </div>
  );
}
